import { useState } from 'react';
import { Pencil, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import type { FormaPagamento } from '../types';

interface Props {
  formas: FormaPagamento[];
  onEdit: (forma: FormaPagamento) => void;
  onInativar: (id: number) => void;
}

export const FormasPagamentoTable = ({ formas, onEdit, onInativar }: Props) => {
  const [inativarId, setInativarId] = useState<number | null>(null);

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center">Recebimento</TableHead>
              <TableHead className="text-center">Pagamento</TableHead>
              <TableHead className="text-center">Venda Prazo</TableHead>
              <TableHead className="text-center">Boleto</TableHead>
              <TableHead className="text-center">TEF</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {formas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  Nenhuma forma de pagamento cadastrada
                </TableCell>
              </TableRow>
            ) : (
              formas.map((forma) => (
                <TableRow key={forma.id}>
                  <TableCell className="font-medium">{forma.descricao || '-'}</TableCell>
                  <TableCell className="text-center">
                    {forma.permiteRecebimento ? <Badge variant="outline" className="text-xs">Sim</Badge> : <span className="text-muted-foreground text-xs">Não</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {forma.permitePagamento ? <Badge variant="outline" className="text-xs">Sim</Badge> : <span className="text-muted-foreground text-xs">Não</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {forma.vendaPrazo ? <Badge variant="outline" className="text-xs">Sim</Badge> : <span className="text-muted-foreground text-xs">Não</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {forma.geraBoleto ? <Badge variant="outline" className="text-xs">Sim</Badge> : <span className="text-muted-foreground text-xs">Não</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {forma.integradoTef ? <Badge variant="outline" className="text-xs">Sim</Badge> : <span className="text-muted-foreground text-xs">Não</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={forma.isAtivo ? 'success' : 'secondary'}>
                      {forma.isAtivo ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(forma)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {forma.isAtivo && (
                        <Button variant="ghost" size="icon" onClick={() => setInativarId(forma.id)}>
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar forma de pagamento</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar esta forma de pagamento?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { onInativar(inativarId); setInativarId(null); } }}>
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
